/* ═══════════════════════════════════════════════════════════════════════
   crAIg's own branding.

   The architecture, stated once so it stays consistent:

     The tenant's colour runs the INTERFACE — buttons, progress, focus
     rings. Their client should feel they are dealing with their own
     accountant, lawyer or plumber, not with a platform.

     crAIg's colour and mark stay HIS — his face, his wordmark, the
     "Let crAIg do it" sign-off. Small, consistent, and never competing
     with the practice for attention.

   That split is what lets one product carry a hundred businesses and
   still accumulate a name of its own.
   ═══════════════════════════════════════════════════════════════════════ */

/**
 * The wordmark. The capital A and I are the whole joke — it's crAIg, an
 * AI, and it reads as a person's name at a glance. Never restyle the
 * casing; it is the mark.
 */
export function Wordmark({
  size = 15,
  muted = false,
}: {
  /**
   * A pixel size, or "inherit" to take the surrounding text's.
   *
   * "inherit" exists because the landing headline is responsive — it
   * clamps between 21px and 36px — and any fixed number would be wrong at
   * one end. The first version passed `undefined` and quietly got the
   * 15px default, so crAIg's own name sat tiny in the middle of his own
   * headline.
   */
  size?: number | "inherit";
  muted?: boolean;
}) {
  return (
    <span
      style={{
        fontSize: size,
        fontWeight: 700,
        // A wordmark at headline scale needs tighter tracking than one at
        // 12px in a footer, or the letters drift apart.
        letterSpacing: size === "inherit" ? "-0.03em" : "-0.02em",
        color: muted ? "var(--ink-2)" : "var(--ink)",
        whiteSpace: "nowrap",
        /*
         * The mark defends its own casing.
         *
         * Dropped into an uppercase heading, this rendered as "CRAIG" —
         * the capital A and I are the entire joke, and without them it is
         * just a word. Enforced here rather than remembered at each call
         * site, because the next uppercase container will not remember.
         */
        textTransform: "none",
        fontVariant: "normal",
      }}
    >
      cr<span style={{ color: "var(--craig)" }}>AI</span>g
    </span>
  );
}

/**
 * The sign-off, shown once per screen at the bottom of the file panel.
 *
 * Deliberately understated. A customer is here to deal with their
 * accountant; crAIg earns recognition by being unobtrusive and useful, not
 * by putting a banner above the practice that hired him.
 */
export function LetCraigDoIt({ className = "" }: { className?: string }) {
  return (
    <div
      className={`flex items-center justify-center gap-1.5 py-3 ${className}`}
      style={{ fontSize: 12, color: "var(--ink-3)" }}
    >
      <CraigMark size={16} />
      <span>
        Let <Wordmark size={12} muted /> do it
      </span>
    </div>
  );
}

/**
 * The mark on its own — crAIg's face reduced to a favicon-sized glyph, for
 * places too small for the full animated face.
 */
export function CraigMark({ size = 20 }: { size?: number }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: size * 0.28,
        background: "var(--craig)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <svg viewBox="0 0 24 24" width={size * 0.66} height={size * 0.66}>
        <rect x="3" y="5" width="18" height="14" rx="2.5" fill="#fff" opacity=".95" />
        <circle cx="9" cy="12" r="1.9" fill="var(--craig)" />
        <circle cx="15" cy="12" r="1.9" fill="var(--craig)" />
      </svg>
    </span>
  );
}
